import { tryCatchSync } from '@/try-catch';

export const formatDateRelative = (date: string, locale: string) => {
  const timeMs = new Date(date).getTime();
  const deltaSeconds = Math.round((Date.now() - timeMs) / 1000);

  if (Math.abs(deltaSeconds) < 60) {
    return new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(0, 'second');
  }

  const cutoffs = [60, 3600, 86400, 86400 * 7, 86400 * 30, 86400 * 365, Infinity];
  const units: Intl.RelativeTimeFormatUnit[] = [
    'second',
    'minute',
    'hour',
    'day',
    'week',
    'month',
    'year',
  ];

  const unitIndex = cutoffs.findIndex((cutoff) => cutoff > Math.abs(deltaSeconds));
  const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1;
  const unit = units[unitIndex];

  if (!divisor || !unit) return '';

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  return rtf.format(-Math.floor(deltaSeconds / divisor), unit);
};

export const dateFormat = {
  withTime: {
    day: 'numeric',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
  dayFirst: { day: 'numeric', month: '2-digit', year: 'numeric' },
  dayAndMonth: { day: 'numeric', month: 'short' },
  dayMonthWithTime: { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' },
} as const satisfies Record<string, Intl.DateTimeFormatOptions>;

export const formatDate = (
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    weekday: 'long',
  },
  locale: string = 'en',
) =>
  new Intl.DateTimeFormat(locale, {
    ...options,
  }).format(new Date(date));

const startOfDay = (date: Date) => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

const isSameCalendarDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const daysUntil = (date: string) =>
  Math.round((new Date(`${date}T00:00`).getTime() - startOfDay(new Date()).getTime()) / 86_400_000);

const getLocaleWeekStartDay = (locale: string) =>
  tryCatchSync(
    () =>
      (new Intl.Locale(locale) as Intl.Locale & { weekInfo?: { firstDay?: number } }).weekInfo
        ?.firstDay,
  ).data ?? 1;

const getStartOfLocaleWeek = (date: Date, firstDay: number) => {
  const weekStart = startOfDay(date);
  const isoWeekday = weekStart.getDay() === 0 ? 7 : weekStart.getDay();
  const daysFromWeekStart = (isoWeekday - firstDay + 7) % 7;
  weekStart.setDate(weekStart.getDate() - daysFromWeekStart);
  return weekStart;
};

export const formatChatMessageTime = (
  date: Date | string | number,
  locale: string,
  labels: { yesterday: string },
) => {
  const messageDate = new Date(date);
  const now = new Date();
  const messageDay = startOfDay(messageDate);
  const today = startOfDay(now);

  const timeOnly = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
  }).format(messageDate);

  if (isSameCalendarDay(messageDay, today)) {
    return timeOnly;
  }

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (isSameCalendarDay(messageDay, yesterday)) {
    return `${labels.yesterday}, ${timeOnly}`;
  }

  const weekStart = getStartOfLocaleWeek(today, getLocaleWeekStartDay(locale));
  if (messageDay >= weekStart) {
    return new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      hour: 'numeric',
      minute: '2-digit',
    }).format(messageDate);
  }

  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  };
  if (messageDate.getFullYear() !== now.getFullYear()) {
    options.year = 'numeric';
  }
  return new Intl.DateTimeFormat(locale, options).format(messageDate);
};
